import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { LogOut, Menu, MessageSquare } from 'lucide-react';
import { RootState } from '../store';
import SessionFeedbackModal from './SessionFeedbackModal';
import { config } from '../config';
import { apiService } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { toDisplayVersion } from '../utils/displayVersion';

interface TopBarProps {
  onSignOut: () => void;
  isSaving?: boolean;
  roomId?: string | null;
}

const TopBar: React.FC<TopBarProps> = ({ onSignOut, isSaving = false, roomId }) => {
  const { showToast } = useToast();
  const userEmail = useSelector((state: RootState) => (state.auth as any)?.user?.email as string | undefined);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const handleFeedbackSubmit = async (formData: FormData) => {
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      // A live call ties feedback to that session (shows up on the session
      // record); otherwise it goes to the tenant-wide feedback inbox.
      if (roomId) {
        await apiService.submitSessionFeedback(roomId, formData);
      } else {
        await apiService.submitFeedback(formData);
      }
      setShowFeedbackModal(false);
      showToast('Feedback submitted');
    } catch (err: any) {
      setFeedbackError(err?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Extract port from WebSocket URL
  // TODO(cleanup): unreferenced today. Kept rather than deleted so removing it is a
  // deliberate, reviewed change instead of a side effect of the lint pass.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getServerPort = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.port || '80';
    } catch {
      return 'Unknown';
    }
  };

  return (
    <>
      {/* The macOS bar: tenant and "Powered by" on one line, the menu button on the right, no shadow. */}
      <div className="flex items-center justify-between bg-signature px-4 py-2.5 text-on-signature">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold">{config.app.name}</span>
          <span className="shrink-0 text-[10px] opacity-75">Powered by Apexia Labs</span>
        </div>

        {/* Account menu (hamburger) — mirrors the desktop apps' overflow menu */}
        <button
          onClick={() => setShowAccountMenu((open) => !open)}
          title="Account menu"
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-control bg-white/[0.14] transition-colors hover:bg-white/20"
        >
          {isSaving ? <span className="animate-spin">⏳</span> : <Menu className="h-[15px] w-[15px]" strokeWidth={2.5} />}
        </button>
      </div>

      {/* Account menu dropdown (mirrors the desktop apps' hamburger menu) */}
      {showAccountMenu && (
        <>
          {/* Transparent backdrop: click anywhere outside closes the menu */}
          <div className="fixed inset-0 z-[60]" onClick={() => setShowAccountMenu(false)} />
          <div className="fixed right-4 top-[54px] z-[70] w-56 bg-surface rounded-control shadow-lg border border-hairline py-1.5">
            {userEmail && (
              <>
                <div className="px-3.5 py-1.5 text-[11px] font-medium text-ink-muted truncate">{userEmail}</div>
                <div className="border-t border-hairline" />
              </>
            )}
            <button
              onClick={() => {
                setShowAccountMenu(false);
                setShowFeedbackModal(true);
              }}
              title="Send feedback"
              className="w-full flex items-center space-x-2 px-3.5 py-2 text-xs text-ink-secondary hover:bg-surface-subtle disabled:text-ink-disabled disabled:cursor-not-allowed"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span>Feedback</span>
            </button>
            <div className="border-t border-hairline my-1 mx-2" />
            <button
              onClick={() => {
                setShowAccountMenu(false);
                onSignOut();
              }}
              disabled={isSaving}
              className="w-full flex items-center space-x-2 px-3.5 py-2 text-xs text-error hover:bg-error-tint disabled:text-ink-disabled disabled:cursor-not-allowed"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>{isSaving ? 'Saving...' : 'Sign Out'}</span>
            </button>
            {/* The app version: here rather than in the bar, as on macOS and WPF; hover shows the full build id */}
            <div
              className="px-3.5 pt-1.5 pb-1 text-[11px] text-ink-muted tabular-nums"
              title={`build ${config.app.version}`}
            >
              Version {toDisplayVersion(config.app.version)}
            </div>
          </div>
        </>
      )}

      {/* Session Feedback Modal */}
      <SessionFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setFeedbackError(null);
        }}
        onSubmit={handleFeedbackSubmit}
        submitting={feedbackSubmitting}
        error={feedbackError}
        isSessionScoped={!!roomId}
      />
    </>
  );
};

export default TopBar;